import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Search, FileText, Clock, CheckCircle, AlertTriangle, Eye, 
  Package, Users, Bell, FileCheck, Receipt, ArrowRight, RefreshCw,
  Building2, Calendar, DollarSign
} from 'lucide-react';
import {
  getCompanies,
  getTimesheetReminders, markReminderReceived,
  getDeliveryNotesV2,
  createClientTimesheet, getClientTimesheets, approveClientTimesheet,
  createSupplierTimesheet, getSupplierTimesheets, approveSupplierTimesheet,
  getTimesheetStats
} from '../lib/api';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  invoiced: 'bg-purple-100 text-purple-800',
  rejected: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800',
  received: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

export const EquipmentManpowerPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [reminders, setReminders] = useState([]);
  const [deliveredDNs, setDeliveredDNs] = useState([]);
  const [clientTimesheets, setClientTimesheets] = useState([]);
  const [supplierTimesheets, setSupplierTimesheets] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Dialog states
  const [createTimesheetOpen, setCreateTimesheetOpen] = useState(false);
  const [viewTimesheetOpen, setViewTimesheetOpen] = useState(false);
  const [selectedDN, setSelectedDN] = useState(null);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  
  // Form state
  const [timesheetForm, setTimesheetForm] = useState({
    delivery_note_id: '',
    period_from: '',
    period_to: '',
    entries: [],
    total_hours: 0,
    total_days: 0,
    subtotal: 0,
    vat_rate: 15,
    notes: '',
    client_signed: false
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const companyId = selectedCompany !== 'all' ? selectedCompany : null;
      
      const [
        companiesRes,
        remindersRes,
        dnsRes,
        clientTsRes,
        supplierTsRes,
        statsRes
      ] = await Promise.all([
        getCompanies(),
        getTimesheetReminders({ company_id: companyId, status: 'pending' }),
        getDeliveryNotesV2({ company_id: companyId, status: 'delivered' }),
        getClientTimesheets({ company_id: companyId }),
        getSupplierTimesheets({ company_id: companyId }),
        getTimesheetStats(companyId)
      ]);
      
      setCompanies(companiesRes.data);
      setReminders(remindersRes.data || []);
      setDeliveredDNs(dnsRes.data?.filter(dn => ['equipment', 'manpower'].includes(dn.dn_type)) || []);
      setClientTimesheets(clientTsRes.data || []);
      setSupplierTimesheets(supplierTsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMarkReminderReceived = async (reminderId) => {
    try {
      await markReminderReceived(reminderId);
      toast.success('Reminder marked as received');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark reminder');
    }
  };

  const handleOpenCreateTimesheet = (dn) => {
    setSelectedDN(dn);
    setTimesheetForm({
      delivery_note_id: dn.id,
      period_from: dn.delivery_date || new Date().toISOString().split('T')[0],
      period_to: new Date().toISOString().split('T')[0],
      entries: dn.items?.map(item => ({
        item_id: item.id || '',
        description: item.description,
        quantity: item.quantity || 0,
        unit: item.unit || 'day',
        basis: 'day',
        rate: item.rate || 0,
        amount: item.amount || 0,
        hours: 0,
        days: 0
      })) || [],
      total_hours: 0,
      total_days: 0,
      subtotal: 0,
      vat_rate: dn.vat_rate || 15,
      notes: '',
      client_signed: false
    });
    setCreateTimesheetOpen(true);
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = [...timesheetForm.entries];
    newEntries[index][field] = value;
    
    // Recalculate amount based on billing basis (days or hours)
    if (['days', 'hours', 'rate', 'basis'].includes(field)) {
      const entry = newEntries[index];
      const qty = entry.basis === 'hour' ? (entry.hours || 0) : (entry.days || 0);
      newEntries[index].amount = qty * (entry.rate || 0);
    }
    
    // Recalculate totals
    const totalDays = newEntries.reduce((sum, e) => sum + (parseFloat(e.days) || 0), 0);
    const totalHours = newEntries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
    const subtotal = newEntries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
    
    setTimesheetForm({
      ...timesheetForm,
      entries: newEntries,
      total_days: totalDays,
      total_hours: totalHours,
      subtotal: subtotal
    });
  };

  const handleCreateTimesheet = async () => {
    try {
      if (!timesheetForm.period_from || !timesheetForm.period_to) {
        toast.error('Period dates are required');
        return;
      }
      
      const response = await createClientTimesheet(timesheetForm);
      toast.success('Client Timesheet created');
      setCreateTimesheetOpen(false);
      setSelectedDN(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    }
  };

  const handleApproveTimesheet = async (tsId) => {
    try {
      const response = await approveClientTimesheet(tsId);
      toast.success(`Timesheet approved! Invoice ${response.data.invoice_number} created automatically.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve timesheet');
    }
  };

  const vatAmount = (timesheetForm.subtotal * timesheetForm.vat_rate / 100);
  const totalAmount = timesheetForm.subtotal + vatAmount;

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  };

  return (
    <div className="space-y-6" data-testid="equipment-manpower-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Equipment & Manpower</h1>
          <p className="text-muted-foreground">Manage timesheets and invoicing workflow</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-[200px]" data-testid="company-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Workflow Overview Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-yellow-600" />
                <div>
                  <div className="text-2xl font-bold">{stats.reminders?.pending || 0}</div>
                  <p className="text-xs text-muted-foreground">Pending Reminders</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold">{stats.client_timesheets?.draft || 0}</div>
                  <p className="text-xs text-muted-foreground">Draft Timesheets</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <div className="text-2xl font-bold">{stats.client_timesheets?.invoiced || 0}</div>
                  <p className="text-xs text-muted-foreground">Invoiced</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold">{(stats.client_timesheets?.total_value || 0).toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">Total Value (SAR)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-orange-600" />
                <div>
                  <div className="text-2xl font-bold">{stats.supplier_timesheets?.pending_approval || 0}</div>
                  <p className="text-xs text-muted-foreground">Supplier Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <div>
                  <div className="text-2xl font-bold">{stats.supplier_timesheets?.blocked || 0}</div>
                  <p className="text-xs text-muted-foreground">Blocked (No Invoice)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Workflow Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">
            <FileCheck className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="reminders">
            <Bell className="h-4 w-4 mr-2" />
            Reminders ({reminders.length})
          </TabsTrigger>
          <TabsTrigger value="client-timesheets">
            <FileText className="h-4 w-4 mr-2" />
            Client Timesheets
          </TabsTrigger>
          <TabsTrigger value="supplier-timesheets">
            <Users className="h-4 w-4 mr-2" />
            Supplier Timesheets
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Equipment & Manpower Workflow</CardTitle>
              <CardDescription>
                Follow this workflow: Delivery Note → Timesheet Reminder → Client Timesheet → Auto Invoice → Supplier Payment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between py-8">
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-blue-100 rounded-full mb-2">
                    <Package className="h-8 w-8 text-blue-600" />
                  </div>
                  <span className="font-medium">1. Delivery Note</span>
                  <span className="text-sm text-muted-foreground">Create DN from Client PO</span>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-yellow-100 rounded-full mb-2">
                    <Bell className="h-8 w-8 text-yellow-600" />
                  </div>
                  <span className="font-medium">2. Timesheet Reminder</span>
                  <span className="text-sm text-muted-foreground">Auto-created on delivery</span>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-green-100 rounded-full mb-2">
                    <FileText className="h-8 w-8 text-green-600" />
                  </div>
                  <span className="font-medium">3. Client Timesheet</span>
                  <span className="text-sm text-muted-foreground">Enter actual hours/days</span>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-purple-100 rounded-full mb-2">
                    <Receipt className="h-8 w-8 text-purple-600" />
                  </div>
                  <span className="font-medium">4. Auto Invoice</span>
                  <span className="text-sm text-muted-foreground">Created on approval</span>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-orange-100 rounded-full mb-2">
                    <DollarSign className="h-8 w-8 text-orange-600" />
                  </div>
                  <span className="font-medium">5. Supplier Payment</span>
                  <span className="text-sm text-muted-foreground">After client invoice</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bell className="h-5 w-5 text-yellow-600" />
                  Pending Timesheet Reminders
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reminders.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No pending reminders</p>
                ) : (
                  <div className="space-y-2">
                    {reminders.slice(0, 5).map(reminder => (
                      <div key={reminder.id} className="flex justify-between items-center p-2 border rounded">
                        <div>
                          <p className="font-medium">{reminder.dn_number}</p>
                          <p className="text-sm text-muted-foreground">{reminder.client_name}</p>
                        </div>
                        <Button size="sm" onClick={() => handleMarkReminderReceived(reminder.id)}>
                          Mark Received
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Ready for Timesheet
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deliveredDNs.filter(dn => !dn.client_timesheet_id).length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No DNs awaiting timesheet</p>
                ) : (
                  <div className="space-y-2">
                    {deliveredDNs.filter(dn => !dn.client_timesheet_id).slice(0, 5).map(dn => (
                      <div key={dn.id} className="flex justify-between items-center p-2 border rounded">
                        <div>
                          <p className="font-medium">{dn.dn_number}</p>
                          <p className="text-sm text-muted-foreground">{dn.client_name} - {dn.dn_type}</p>
                        </div>
                        <Button size="sm" onClick={() => handleOpenCreateTimesheet(dn)}>
                          Create Timesheet
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Timesheet Reminders</CardTitle>
              <CardDescription>
                Reminders are auto-created when delivery notes are marked as delivered for Equipment/Manpower
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : reminders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No pending reminders. Reminders are created automatically when DNs are delivered.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">DN Number</th>
                        <th className="pb-3 font-medium">Client</th>
                        <th className="pb-3 font-medium">Type</th>
                        <th className="pb-3 font-medium">Company</th>
                        <th className="pb-3 font-medium">Created</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reminders.map(reminder => (
                        <tr key={reminder.id} className="border-b hover:bg-muted/50">
                          <td className="py-3 font-medium">{reminder.dn_number}</td>
                          <td className="py-3">{reminder.client_name}</td>
                          <td className="py-3 capitalize">{reminder.dn_type}</td>
                          <td className="py-3">{getCompanyName(reminder.company_id)}</td>
                          <td className="py-3">{new Date(reminder.created_at).toLocaleDateString()}</td>
                          <td className="py-3">
                            <Badge className={statusColors[reminder.status]}>
                              {reminder.status}
                            </Badge>
                          </td>
                          <td className="py-3">
                            {reminder.status === 'pending' && (
                              <Button size="sm" onClick={() => handleMarkReminderReceived(reminder.id)}>
                                Mark Received
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Client Timesheets Tab */}
        <TabsContent value="client-timesheets" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Client Timesheets</CardTitle>
                <CardDescription>
                  Record actual hours/days from client. Approval auto-creates invoice.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : clientTimesheets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No client timesheets yet. Create one from a delivered DN.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">Timesheet #</th>
                        <th className="pb-3 font-medium">DN #</th>
                        <th className="pb-3 font-medium">Client</th>
                        <th className="pb-3 font-medium">Type</th>
                        <th className="pb-3 font-medium">Period</th>
                        <th className="pb-3 font-medium">Total</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientTimesheets.map(ts => (
                        <tr key={ts.id} className="border-b hover:bg-muted/50" data-testid={`client-ts-${ts.id}`}>
                          <td className="py-3 font-medium">{ts.timesheet_number}</td>
                          <td className="py-3">{ts.dn_number}</td>
                          <td className="py-3">{ts.client_name}</td>
                          <td className="py-3 capitalize">{ts.dn_type}</td>
                          <td className="py-3">{ts.period_from} - {ts.period_to}</td>
                          <td className="py-3">{ts.currency} {ts.total?.toLocaleString()}</td>
                          <td className="py-3">
                            <Badge className={statusColors[ts.status]}>
                              {ts.status}
                            </Badge>
                          </td>
                          <td className="py-3">
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedTimesheet(ts);
                                  setViewTimesheetOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {ts.status === 'draft' && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleApproveTimesheet(ts.id)}
                                  data-testid={`approve-ts-${ts.id}`}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve & Invoice
                                </Button>
                              )}
                              {ts.status === 'invoiced' && ts.invoice_id && (
                                <Badge className="bg-purple-100 text-purple-800">
                                  <Receipt className="h-3 w-3 mr-1" />
                                  Invoiced
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Supplier Timesheets Tab */}
        <TabsContent value="supplier-timesheets" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Supplier Timesheets</CardTitle>
              <CardDescription>
                Supplier timesheets can only be approved AFTER client invoice is created
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : supplierTimesheets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No supplier timesheets yet. These are created against approved client timesheets.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">Timesheet #</th>
                        <th className="pb-3 font-medium">Supplier</th>
                        <th className="pb-3 font-medium">Client TS #</th>
                        <th className="pb-3 font-medium">Period</th>
                        <th className="pb-3 font-medium">Total</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Can Approve</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplierTimesheets.map(ts => (
                        <tr key={ts.id} className="border-b hover:bg-muted/50">
                          <td className="py-3 font-medium">{ts.timesheet_number}</td>
                          <td className="py-3">{ts.supplier_name}</td>
                          <td className="py-3">{ts.client_timesheet_number}</td>
                          <td className="py-3">{ts.period_from} - {ts.period_to}</td>
                          <td className="py-3">{ts.currency} {ts.total?.toLocaleString()}</td>
                          <td className="py-3">
                            <Badge className={statusColors[ts.status]}>
                              {ts.status}
                            </Badge>
                          </td>
                          <td className="py-3">
                            {ts.can_approve ? (
                              <Badge className="bg-green-100 text-green-800">Yes</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800">No - Awaiting Client Invoice</Badge>
                            )}
                          </td>
                          <td className="py-3">
                            {ts.can_approve && ts.status === 'draft' && (
                              <Button
                                size="sm"
                                onClick={() => approveSupplierTimesheet(ts.id).then(() => {
                                  toast.success('Supplier timesheet approved');
                                  fetchData();
                                })}
                              >
                                Approve
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Client Timesheet Dialog */}
      <Dialog open={createTimesheetOpen} onOpenChange={setCreateTimesheetOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Client Timesheet</DialogTitle>
          </DialogHeader>
          
          {selectedDN && (
            <div className="space-y-4">
              {/* DN Info */}
              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">DN Number:</span>
                      <p className="font-medium">{selectedDN.dn_number}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Client:</span>
                      <p className="font-medium">{selectedDN.client_name}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <p className="font-medium capitalize">{selectedDN.dn_type}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">PO Number:</span>
                      <p className="font-medium">{selectedDN.client_po_number}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Period */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Period From *</Label>
                  <Input
                    type="date"
                    value={timesheetForm.period_from}
                    onChange={(e) => setTimesheetForm({...timesheetForm, period_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Period To *</Label>
                  <Input
                    type="date"
                    value={timesheetForm.period_to}
                    onChange={(e) => setTimesheetForm({...timesheetForm, period_to: e.target.value})}
                  />
                </div>
              </div>

              {/* Entries */}
              <div>
                <Label className="text-base font-semibold">Actual Hours/Days from Client</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Enter the actual working days/hours as confirmed by the client
                </p>
                
                <div className="space-y-2">
                  {timesheetForm.entries.map((entry, idx) => (
                    <Card key={idx}>
                      <CardContent className="pt-3">
                        <div className="grid grid-cols-8 gap-2 items-center">
                          <div className="col-span-2">
                            <Label className="text-xs">Description</Label>
                            <Input
                              value={entry.description}
                              onChange={(e) => handleEntryChange(idx, 'description', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Basis</Label>
                            <Select
                              value={entry.basis || 'day'}
                              onValueChange={(v) => handleEntryChange(idx, 'basis', v)}
                            >
                              <SelectTrigger className="h-8" data-testid={`entry-basis-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="day">Daily</SelectItem>
                                <SelectItem value="hour">Hourly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {(entry.basis || 'day') === 'hour' ? (
                            <div>
                              <Label className="text-xs">Hours</Label>
                              <Input
                                type="number"
                                value={entry.hours}
                                onChange={(e) => handleEntryChange(idx, 'hours', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                data-testid={`entry-hours-${idx}`}
                              />
                            </div>
                          ) : (
                            <div>
                              <Label className="text-xs">Days</Label>
                              <Input
                                type="number"
                                value={entry.days}
                                onChange={(e) => handleEntryChange(idx, 'days', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                data-testid={`entry-days-${idx}`}
                              />
                            </div>
                          )}
                          <div className="col-span-2">
                            <Label className="text-xs">Rate / {(entry.basis || 'day') === 'hour' ? 'Hour' : 'Day'}</Label>
                            <Input
                              type="number"
                              value={entry.rate}
                              onChange={(e) => handleEntryChange(idx, 'rate', parseFloat(e.target.value) || 0)}
                              className="h-8"
                              data-testid={`entry-rate-${idx}`}
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Amount</Label>
                            <div className="h-8 flex items-center font-medium">
                              SAR {(entry.amount || 0).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span>Total Days:</span>
                    <span className="font-medium">{timesheetForm.total_days}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Hours:</span>
                    <span className="font-medium">{timesheetForm.total_hours}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="font-medium">SAR {timesheetForm.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>VAT ({timesheetForm.vat_rate}%):</span>
                    <span className="font-medium">SAR {vatAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-bold">Total:</span>
                    <span className="font-bold">SAR {totalAmount.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={timesheetForm.notes}
                  onChange={(e) => setTimesheetForm({...timesheetForm, notes: e.target.value})}
                  placeholder="Any additional notes..."
                  rows={2}
                />
              </div>

              {/* Client Signed */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="client-signed"
                  checked={timesheetForm.client_signed}
                  onChange={(e) => setTimesheetForm({...timesheetForm, client_signed: e.target.checked})}
                />
                <Label htmlFor="client-signed">Client has signed/approved the timesheet</Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateTimesheetOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTimesheet} data-testid="create-timesheet-btn">
              Create Timesheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Timesheet Dialog */}
      <Dialog open={viewTimesheetOpen} onOpenChange={setViewTimesheetOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Timesheet Details</DialogTitle>
          </DialogHeader>
          
          {selectedTimesheet && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Timesheet Number</Label>
                  <p className="font-medium">{selectedTimesheet.timesheet_number}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge className={statusColors[selectedTimesheet.status]}>
                    {selectedTimesheet.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">DN Number</Label>
                  <p className="font-medium">{selectedTimesheet.dn_number}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Client PO</Label>
                  <p className="font-medium">{selectedTimesheet.client_po_number}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Client</Label>
                  <p className="font-medium">{selectedTimesheet.client_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p className="font-medium capitalize">{selectedTimesheet.dn_type}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Period</Label>
                  <p className="font-medium">{selectedTimesheet.period_from} to {selectedTimesheet.period_to}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Total Days</Label>
                  <p className="font-medium">{selectedTimesheet.total_days}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-muted-foreground">Subtotal</Label>
                  <p className="font-medium">{selectedTimesheet.currency} {selectedTimesheet.subtotal?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">VAT</Label>
                  <p className="font-medium">{selectedTimesheet.currency} {selectedTimesheet.vat_amount?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Total</Label>
                  <p className="font-bold">{selectedTimesheet.currency} {selectedTimesheet.total?.toLocaleString()}</p>
                </div>
              </div>

              {selectedTimesheet.invoice_id && (
                <div className="p-3 bg-purple-50 rounded border border-purple-200">
                  <Label className="text-muted-foreground">Invoice Generated</Label>
                  <p className="font-medium text-purple-700">Invoice ID: {selectedTimesheet.invoice_id}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentManpowerPage;
